import stageKey from "Utils/stageKey";

const DAY = 86400000;

const ms = d => {
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : 0;
};

const lower = v => (v || "").toString().trim().toLowerCase();

// Where a reply stops being what the person wrote and starts being the mail
// they replied to. Everything from here down is history, not their words.
const QUOTE_MARKER = /<blockquote|gmail_quote|-----\s*Original Message\s*-----|^\s*On\b.{0,160}\bwrote:/im;

// Boilerplate we append ourselves. It is always the last thing in the body, so
// left in place it stands in for whatever the sender actually said last.
const FOOTER_LINE = /unsubscribe|click here|don'?t want to hear from me|opt out of|update your preferences/i;

// Bodies arrive as HTML with no newlines, so splitting on "\n" alone returned
// the whole document as one "line".
const visibleLines = body => {
  let text = String(body || "");
  const cut = text.search(QUOTE_MARKER);
  if (cut > 0) text = text.slice(0, cut);
  return text
    .replace(/<br\s*\/?>|<\/p>|<\/div>|<\/li>|<\/tr>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&(apos|#39);/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&amp;/gi, "&")
    .split("\n")
    .map(l => l.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

// What the sender wrote, with the quoted history removed. Footer text is kept:
// a reply whose own words are "unsubscribe me" must still count.
const visibleText = body => visibleLines(body).join("\n");

// Latest line that carries meaning -- signature and unsubscribe boilerplate
// below a question must not hide its trailing "?".
const lastLine = body => {
  const lines = visibleLines(body);
  while (lines.length && FOOTER_LINE.test(lines[lines.length - 1])) lines.pop();
  return lines[lines.length - 1] || "";
};

const UNSUBSCRIBE_RE = /unsubscribe|remove me|take me off|stop emailing|opt me out/i;

/*
 * inboxFindings is pure: no store imports. Every rule reads only what's passed
 * in and stays silent when its input is missing (openThread messages, the
 * inbox list, etc.) rather than guessing.
 *
 * Note: li_accepted_no_message (spec table) is intentionally not implemented --
 * nothing in inboxV2Store's liReply payload records whether the contact
 * accepted the LinkedIn connection request, so the rule has no input to fire on.
 */
const inboxFindings = (
  stats = {},
  {
    openThread = false,
    activeSequences = [],
    feed = [],
    rail = [],
    inboxes = [],
    contactEmail = "",
    contactStage = "",
    now = Date.now(),
    actions = {}
  } = {}
) => {
  if (!Array.isArray(inboxes) || !inboxes.length) return [];

  const out = [];
  const {
    allCount = 0,
    unreadCount = 0,
    positiveCount = 0,
    autoReplyCount = 0
  } = stats;

  if (positiveCount > 0 && unreadCount > 0) {
    out.push({
      tone: "hot",
      key: "positive_waiting",
      label: "Positive replies waiting",
      text: "Replies classified positive are revenue already earned. Answer them first.",
      actions: actions.showPositiveUnread
        ? [{ label: "Show them", run: actions.showPositiveUnread }]
        : []
    });
  }

  const railList = Array.isArray(rail) ? rail : [];
  // Rail rows are {task, sequence, contact} wrappers -- the reply/sent fields
  // live on the task, so reading them flat off the row made this always empty.
  const cold = railList.filter(row => {
    const item = row?.task || row;
    if (!(+item?.replyCount > 0)) return false;
    const replyAt = ms(item?.replyDate);
    if (!replyAt || now - replyAt <= 2 * DAY) return false;
    const sentAt = ms(item?.sentDate);
    return !(sentAt && sentAt > replyAt);
  });
  if (cold.length) {
    const oldest = cold.reduce((a, b) => (ms(a?.replyDate) < ms(b?.replyDate) ? a : b));
    out.push({
      tone: "warn",
      key: "reply_cold",
      label: `${cold.length} repl${cold.length === 1 ? "y" : "ies"} going cold`,
      text: "Over 48 hours unanswered. Open the oldest.",
      actions: actions.selectContact
        ? [{ label: "Open oldest", run: () => actions.selectContact(oldest) }]
        : []
    });
  }

  if (allCount > 0 && autoReplyCount / allCount >= 0.3) {
    out.push({
      tone: "neutral",
      key: "auto_replies",
      label: "Auto-replies mixed in",
      text: `Out-of-office and auto-replies are ${Math.round(
        (autoReplyCount / allCount) * 100
      )}% of replies. Filter to real ones.`,
      actions: actions.filterRealReplies
        ? [{ label: "Filter to real replies", run: actions.filterRealReplies }]
        : []
    });
  }

  const disconnected = (Array.isArray(inboxes) ? inboxes : []).filter(
    inbox => inbox?.accessRevokedBySystem === true
  );
  disconnected.forEach(inbox => {
    out.push({
      tone: "bad",
      key: "inbox_disconnected",
      label: `Inbox disconnected: ${inbox?.email || ""}`,
      text: "Sequences on it are stalled.",
      actions: actions.askCopilot
        ? [
            {
              label: "Which sequences are affected?",
              run: () =>
                actions.askCopilot(
                  `Which sequences use the inbox ${inbox?.email || ""}? It is disconnected. List them, do not change anything.`
                )
            }
          ]
        : []
    });
  });

  if (openThread) {
    const messages = Array.isArray(feed) ? feed : [];
    const inbound = messages.filter(m => m?.direction === "in" || m?.dir === "in");
    const outbound = messages.filter(m => m?.direction === "out" || m?.dir === "out");
    const latestInbound = inbound.length
      ? inbound.reduce((a, b) => (ms(a?.date) > ms(b?.date) ? a : b))
      : null;
    const latestOutbound = outbound.length
      ? outbound.reduce((a, b) => (ms(a?.date) > ms(b?.date) ? a : b))
      : null;

    if (
      latestInbound &&
      UNSUBSCRIBE_RE.test(visibleText(latestInbound.body)) &&
      stageKey(contactStage) !== "OPTED_OUT"
    ) {
      out.push({
        tone: "bad",
        key: "unsubscribe_in_words",
        label: "They asked to stop",
        text: "The reply asks to unsubscribe. Opt them out so nothing else sends.",
        actions: actions.askCopilot
          ? [
              {
                label: "Opt out",
                run: () =>
                  actions.askCopilot(
                    "Opt out the contact from the open thread; they asked to stop in their reply. Ask me to confirm first."
                  )
              }
            ]
          : []
      });
    }

    if (
      latestOutbound &&
      /\?$/.test(lastLine(latestOutbound.body)) &&
      ms(latestOutbound.date) &&
      now - ms(latestOutbound.date) > 3 * DAY &&
      (!latestInbound || ms(latestInbound.date) <= ms(latestOutbound.date))
    ) {
      out.push({
        tone: "warn",
        key: "question_unanswered",
        label: "Your question went unanswered",
        text: "3+ days with no reply to your question. A short nudge works better than the next step.",
        actions: actions.askCopilot
          ? [
              {
                label: "Nudge",
                run: () =>
                  actions.askCopilot(
                    "Draft a two-line nudge for the open thread referencing my unanswered question. Show me, do not send."
                  )
              }
            ]
          : []
      });
    }

    if (
      latestInbound?.from &&
      contactEmail &&
      lower(latestInbound.from) !== lower(contactEmail) &&
      !actions.isOwnInbox?.(latestInbound.from)
    ) {
      out.push({
        tone: "hot",
        key: "referral_reply",
        label: "Reply from someone else",
        text: `${latestInbound.from} answered instead. Create them as a contact and continue there.`,
        actions: actions.askCopilot
          ? [
              {
                label: "Create contact",
                run: () =>
                  actions.askCopilot(
                    `The latest reply in the open thread came from ${latestInbound.from}, not the contact. Create a contact for them from the thread. Ask me to confirm first.`
                  )
              }
            ]
          : []
      });
    }

    if ((activeSequences || []).length) {
      const names = activeSequences.map(seq => seq.name);
      out.push({
        tone: "warn",
        key: "replied_enrolled",
        label: `Replied but still in ${names.join(", ")}`,
        text: "They answered, so further automated steps will read as spam. Pause them in the sequence.",
        actions: actions.pauseInSequence
          ? [{ label: "Pause them", run: () => actions.pauseInSequence(activeSequences[0]) }]
          : []
      });
    }

    out.push({
      tone: "neutral",
      key: "open_thread",
      label: "This thread",
      text: "I can draft the reply, or read it and tell you the next step: book, answer, snooze, or ask for the right person.",
      actions: []
    });
  }

  return out;
};

export default inboxFindings;
